import {
  NORM_KINDS,
  type NormItemCard,
  type NormItemRow,
  type NormReleaseSummary,
  type NormResourceRow,
  RESOURCE_KINDS,
} from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Input } from '../../ui/form';
import { Page, PageHeader, Panel, TitleBlock } from '../../ui/layout';
import { hours, qty, sum } from '../estimates/lib';
import { ReleaseFilesPanel } from './Library';
import { NormStatusBadge } from './NormsPage';

/** Карточка нормы: состав ресурсов с ценами, история цен по выпускам, аналоги, где применена (СМ-03, БЗ-17) */
export function NormItemPage() {
  const { itemId } = useParams({ strict: false }) as { itemId: string };
  const q = useQuery({
    queryKey: ['norms', 'item', itemId],
    queryFn: () => api<NormItemCard>(`/norms/items/${itemId}`),
  });
  useCrumbs(
    q.data
      ? [
          { label: q.data.releaseCode, to: `/norms/releases/${q.data.releaseId}` },
          { label: q.data.code, title: q.data.name },
        ]
      : null,
    q.error,
  );
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const it = q.data;
  if (typeof document !== 'undefined') document.title = `${it.code} — EPC Pilot AI`;
  return (
    <Page>
      <PageHeader title={it.code} subtitle={it.name}>
        <span className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
          <Badge>{it.releaseCode}</Badge>
          <span>измеритель: {it.unit}</span>
          {it.collection ? <span>сборник {it.collection}</span> : null}
          <span>вид затрат: {it.costKind}</span>
          {it.usageCount ? <span>применена {it.usageCount} раз</span> : null}
        </span>
      </PageHeader>
      <div className="flex flex-col gap-6">
        <TitleBlock
          items={[
            {
              label: 'Прямые затраты на измеритель',
              value: it.unitDirect !== null ? `${sum(it.unitDirect)} ₸` : '—',
              note: 'без коэффициентов, по ценам последнего выпуска',
            },
            { label: 'Трудоёмкость', value: hours(it.laborHours), note: 'чел.-ч на измеритель' },
            { label: 'Ресурсов в составе', value: it.resources.length },
          ]}
        />
        <Panel title="Состав ресурсов" meta="норма расхода на измеритель" bodyClassName="p-0">
          <ul className="divide-y divide-line md:hidden">
            {it.resources.map((r) => (
              <li key={`${r.code}-${r.unit}`} className="px-4 py-3 text-[13px]">
                <span className="num text-ink-2">{r.code}</span>
                <span className="block text-ink [overflow-wrap:anywhere]">{r.name}</span>
                <span className="num block text-ink-2">
                  {qty(r.norm)} {r.unit}
                  {r.price !== null ? ` × ${sum(r.price)} = ${sum(r.amount)} ₸` : ' · цены нет'}
                </span>
              </li>
            ))}
          </ul>
          <table className="hidden w-full text-[13px] md:table">
            <thead>
              <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                <th className="py-2.5 pr-2 pl-5 font-medium">Код</th>
                <th className="py-2.5 pr-2 font-medium">Ресурс</th>
                <th className="py-2.5 pr-2 font-medium">Вид</th>
                <th className="py-2.5 pr-2 font-medium">Ед.</th>
                <th className="py-2.5 pr-2 text-right font-medium">Норма</th>
                <th className="py-2.5 pr-2 text-right font-medium">Цена, ₸</th>
                <th className="py-2.5 pr-5 text-right font-medium">Сумма, ₸</th>
              </tr>
            </thead>
            <tbody>
              {it.resources.map((r) => (
                <tr key={`${r.code}-${r.unit}`} className="border-t border-line">
                  <td className="num py-2 pr-2 pl-5 whitespace-nowrap text-ink-2">{r.code}</td>
                  <td className="py-2 pr-2 [overflow-wrap:anywhere]">{r.name}</td>
                  <td className="py-2 pr-2 whitespace-nowrap text-ink-2">
                    {RESOURCE_KINDS[r.kind as keyof typeof RESOURCE_KINDS] ?? r.kind}
                  </td>
                  <td className="py-2 pr-2 whitespace-nowrap">{r.unit}</td>
                  <td className="num py-2 pr-2 text-right">{qty(r.norm)}</td>
                  <td className="num py-2 pr-2 text-right">
                    {r.price !== null ? sum(r.price) : <span className="text-warning">нет цены</span>}
                  </td>
                  <td className="num py-2 pr-5 text-right">{sum(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
        <div className="grid gap-6 lg:grid-cols-2">
          <Panel title="История цен по выпускам" meta="СМ-03">
            {it.history.length > 1 ? (
              <ul className="flex flex-col gap-1 text-[13px]">
                {it.history.map((h) => (
                  <li key={h.releaseId} className="flex items-center gap-3">
                    <span className="flex-1">{h.releaseCode}</span>
                    <span className="num">{h.unitDirect !== null ? `${sum(h.unitDirect)} ₸` : '—'}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[13px] text-ink-2">
                Норма есть только в одном выпуске; история появится с загрузкой других редакций.
              </p>
            )}
          </Panel>
          <Panel title="Аналоги в той же таблице" meta="СМ-03">
            {it.analogs.length ? (
              <ul className="flex flex-col gap-1 text-[13px]">
                {it.analogs.map((a) => (
                  <li key={a.id}>
                    <Link
                      to="/norms/items/$itemId"
                      params={{ itemId: a.id }}
                      className="inline-flex min-h-[44px] items-center text-accent-text underline md:min-h-0"
                    >
                      <span className="num">{a.code}</span>
                    </Link>{' '}
                    {a.name} <span className="text-ink-2">({a.unit})</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[13px] text-ink-2">Других норм этой таблицы в базе пока нет.</p>
            )}
          </Panel>
        </div>
        <Panel title="Где применена" meta="БЗ-17: сметы, посчитанные по этой норме" bodyClassName="p-0">
          {it.usedIn.length ? (
            <ul className="divide-y divide-line">
              {it.usedIn.map((u) => (
                <li key={u.estimateId}>
                  <Link
                    to="/estimates/$estimateId"
                    params={{ estimateId: u.estimateId }}
                    className="flex min-h-[44px] flex-wrap items-center gap-3 px-5 py-3 text-[13px] hover:bg-surface-2"
                  >
                    <Badge>{u.projectCode}</Badge>
                    <span className="num">{u.estimateCode}</span>
                    <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{u.estimateName}</span>
                    <span className="num text-ink-2">{u.items} поз.</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-5 py-4 text-[13px] text-ink-2">В доступных вам сметах норма не применялась.</p>
          )}
        </Panel>
      </div>
    </Page>
  );
}

interface ReleaseView {
  release: NormReleaseSummary;
  items: NormItemRow[];
  resources: NormResourceRow[];
  itemsTotal: number;
  resourcesTotal: number;
}

/** Карточка выпуска: нормы и ресурсы с поиском и постраничной выдачей */
export function NormReleasePage() {
  const { releaseId } = useParams({ strict: false }) as { releaseId: string };
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 100;
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(typed);
      setOffset(0);
    }, 300);
    return () => clearTimeout(t);
  }, [typed]);
  const r = useQuery({
    queryKey: ['norms', 'release', releaseId, q, offset],
    queryFn: () =>
      api<ReleaseView>(`/norms/releases/${releaseId}`, { query: { q: q || undefined, offset, limit } }),
  });
  useCrumbs(r.data ? [{ label: r.data.release.code, title: r.data.release.name }] : null, r.error);
  if (r.isPending) return <Spinner />;
  if (r.isError) return <ErrorBox error={r.error} />;
  const { release, items, resources, itemsTotal, resourcesTotal } = r.data;
  const total = Math.max(itemsTotal, resourcesTotal);
  return (
    <Page>
      <PageHeader title={release.code} subtitle={release.name}>
        <span className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
          <NormStatusBadge status={release.status} />
          <span>{NORM_KINDS[release.kind] ?? release.kind}</span>
          {release.priceZone ? <span>· зона {release.priceZone}</span> : null}
          {release.priceLevel ? <span>· {release.priceLevel}</span> : null}
          {release.statusSource ? <span>· источник статуса: {release.statusSource}</span> : null}
        </span>
        <div className="mt-3 max-w-xl">
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Поиск по коду или словам в этом выпуске"
            aria-label="Поиск в выпуске"
          />
        </div>
      </PageHeader>
      <div className="flex flex-col gap-6">
        <ReleaseFilesPanel releaseId={releaseId} />
        {items.length ? (
          <Panel title="Нормы и расценки" meta={`${itemsTotal}`} bodyClassName="p-0">
            <ul className="divide-y divide-line">
              {items.map((it) => (
                <li key={it.id}>
                  <Link
                    to="/norms/items/$itemId"
                    params={{ itemId: it.id }}
                    className="flex min-h-[44px] flex-col gap-1 px-5 py-3 hover:bg-surface-2 md:flex-row md:items-center md:gap-4"
                  >
                    <span className="num w-[150px] shrink-0 text-[13px] text-ink">{it.code}</span>
                    <span className="min-w-0 flex-1 text-[14px] [overflow-wrap:anywhere]">{it.name}</span>
                    <span className="flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
                      <span>{it.unit}</span>
                      {it.laborHours ? <span className="num">{hours(it.laborHours)} чел.-ч</span> : null}
                      <span className="num text-ink">
                        {it.unitDirect !== null ? `${sum(it.unitDirect)} ₸` : ''}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}
        {resources.length ? (
          <Panel title="Ресурсы и цены" meta={`${resourcesTotal}`} bodyClassName="p-0">
            <ul className="divide-y divide-line">
              {resources.map((x) => (
                <li
                  key={x.id}
                  className="flex min-h-[44px] flex-col gap-1 px-5 py-3 md:flex-row md:items-center md:gap-4"
                >
                  <span className="num w-[150px] shrink-0 text-[13px] text-ink">{x.code}</span>
                  <span className="min-w-0 flex-1 text-[14px] [overflow-wrap:anywhere]">{x.name}</span>
                  <span className="flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
                    <Badge>{RESOURCE_KINDS[x.kind as keyof typeof RESOURCE_KINDS] ?? x.kind}</Badge>
                    <span>{x.unit}</span>
                    <span className="num text-ink">{x.price !== null ? `${sum(x.price)} ₸` : ''}</span>
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}
        {!items.length && !resources.length ? (
          <EmptyState title="Ничего не найдено">В выпуске нет записей по этому запросу.</EmptyState>
        ) : null}
        {total > limit ? (
          <div className="flex items-center justify-between gap-3 text-[13px] text-ink-2">
            <Button
              className="min-h-[44px] md:min-h-0"
              variant="secondary"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              Назад
            </Button>
            <span>
              {offset + 1}–{Math.min(offset + limit, total)} из {total}
            </span>
            <Button
              className="min-h-[44px] md:min-h-0"
              variant="secondary"
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
            >
              Дальше
            </Button>
          </div>
        ) : null}
      </div>
    </Page>
  );
}
